import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Star, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const AgentOfTheMonthAdmin = () => {
  const [current, setCurrent] = useState(null);
  const [month, setMonth] = useState('');
  const [agents, setAgents] = useState([]);
  const [slug, setSlug] = useState('');
  const [statLine, setStatLine] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [{ data: aotm }, { data: sites }] = await Promise.all([
        api.get('/careers/agent-of-the-month'),
        api.get('/agent-sites/public/list'),
      ]);
      setCurrent(aotm.agent);
      setMonth(aotm.month);
      setAgents(Array.isArray(sites) ? sites.filter(s => s.active) : []);
      if (aotm.agent?.slug) setSlug(aotm.agent.slug);
    } catch {
      toast.error('Could not load Agent of the Month');
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!slug) { toast.error('Pick an agent'); return; }
    setLoading(true);
    try {
      await api.post('/admin/agent-of-the-month', { agent_slug: slug, stat_line: statLine });
      toast.success('Agent of the Month updated');
      await load();
    } catch {
      toast.error('Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Clear the manual override and revert to auto-pick?')) return;
    setLoading(true);
    try {
      await api.delete('/admin/agent-of-the-month');
      setStatLine('');
      toast.success('Reverted to auto-pick');
      await load();
    } catch {
      toast.error('Failed to clear');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-[#e6dec7] rounded-lg p-5" data-testid="aotm-admin">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-[#BCA480]" />
          <h3 className="font-bold text-sm text-[#434345]">Agent of the Month ({month || '—'})</h3>
        </div>
        <Button size="sm" variant="ghost" onClick={load} data-testid="aotm-refresh">
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>

      {current ? (
        <div className="flex items-center gap-3 bg-[#faf8f4] rounded p-3 mb-4" data-testid="aotm-current">
          {current.photo && (
            <img src={current.photo} alt={current.name} className="w-12 h-12 rounded-full object-cover border border-[#BCA480]/40" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#434345] truncate">{current.name}</div>
            <div className="text-xs text-gray-500 truncate">{current.stat_line}</div>
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-500 mb-4">
          No agent currently featured. Pick one below or wait for an auto-pick once transactions close this month.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Agent</Label>
          <select
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="w-full h-9 px-2 border border-gray-300 rounded text-sm bg-white"
            data-testid="aotm-select"
          >
            <option value="">— Select —</option>
            {agents.map((a) => (
              <option key={a.slug} value={a.slug}>{a.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Stat line (optional)</Label>
          <Input
            value={statLine}
            onChange={(e) => setStatLine(e.target.value)}
            placeholder="e.g., 4 closings this month"
            className="h-9 text-sm"
            data-testid="aotm-statline"
          />
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button size="sm" onClick={save} disabled={loading} className="bg-[#BCA480] hover:bg-[#a89470]" data-testid="aotm-save">
          Set / Update
        </Button>
        <Button size="sm" variant="outline" onClick={clear} disabled={loading} data-testid="aotm-clear">
          <Trash2 className="h-3 w-3 mr-1" /> Revert to auto-pick
        </Button>
      </div>
    </div>
  );
};

export default AgentOfTheMonthAdmin;
